<template>
  <BaseModalEditCreate
    :mode="mode"
    :is-modified="isModified"
    @dismiss-changes="onDismissChanges"
    @save="onSave">
    <template #form>
      <div class="space-y-4">
        <div>
          <label for="name" class="text-sm font-medium">{{ $t('groups.name') }}</label>
          <BaseInput
            v-model="modifiedData.name"
            color="base"
            :disabled="!modifiedData.isFromManager"
            show-error-as-tooltip
            :error="$v.name.required.$invalid ? $v.name.required.$message : ''" />
        </div>
        <div>
          <label for="maxLevel" class="text-sm font-medium">{{ $t('groups.maxLevel') }}</label>
          <BaseInput
            v-model="modifiedData.maxLevel"
            type="number"
            color="base"
            :disabled="!modifiedData.isFromManager" />
        </div>
        <BaseSelectDropdown
          :items="filteredDropDownMWAccountGroups"
          :selected-item="selectedAccountGroup"
          :label="$t('groups.accountGroup')"
          :disable="!modifiedData.isFromManager"
          @update:selected="handleAccountGroupSelection"
          show-error-as-tooltip
          :error="
            $v.mwAccountGroupId.required.$invalid ? $v.mwAccountGroupId.required.$message : ''
          "
          clearable
          name="alterCodeType" />

        <BaseToggle
          :label="$t('groups.system')"
          name="isSystem"
          light
          v-model="modifiedData.isSystem"
          :disable="!modifiedData.isFromManager" />

        <BaseToggle
          light
          :label="$t('groups.active')"
          name="isActive"
          v-model="modifiedData.isActive"
          :disable="!modifiedData.isFromManager" />
      </div>
    </template>
  </BaseModalEditCreate>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useLookupsStore } from '@/stores/lookups';
import useVuelidate from '@vuelidate/core';
import { helpers, required } from '@vuelidate/validators';
import { deepCopy } from '@/utils/data';
import BaseModalEditCreate from '../common/BaseModalEditCreate.vue';
import BaseInput from '../common/BaseInput.vue';
import BaseSelectDropdown from '../common/BaseSelectDropdown.vue';
import BaseToggle from '../common/BaseToggle.vue';
import { RecordMode, type SelectDropdownItem } from '@/types/Base';
import type { Group } from '@/types/Groups';
import { useI18n } from 'vue-i18n';

const emit = defineEmits(['closeModal', 'save']);

const props = withDefaults(
  defineProps<{
    mode: 'add' | 'edit';
    group: Group;
    category: string;
  }>(),
  {}
);

const { dropdownMWAccountGroupsMaterials } = storeToRefs(useLookupsStore());
const { getDropdownMWAccountGroupsByCategory } = useLookupsStore();

const modifiedData = ref<Group>(deepCopy<Group>(props.group));
const isModified = ref<boolean>(false);

onMounted(async () => {
  props.category == 'material'
    ? (filteredDropDownMWAccountGroups.value = dropdownMWAccountGroupsMaterials.value)
    : (filteredDropDownMWAccountGroups.value = getDropdownMWAccountGroupsByCategory(
        props.category
      ));
  initDropDowns();
});

const filteredDropDownMWAccountGroups = ref<SelectDropdownItem[]>([]);
const selectedAccountGroup = ref<SelectDropdownItem | null>(null);

function initDropDowns() {
  if (modifiedData.value) {
    if (props.category == 'materials') {
      const accountGroup = filteredDropDownMWAccountGroups.value.find(
        (item) => item.value == modifiedData.value?.mwAccountGroupId
      );
      if (accountGroup) selectedAccountGroup.value = accountGroup;
      else selectedAccountGroup.value = null;
    } else {
      const accountGroup = filteredDropDownMWAccountGroups?.value.find(
        (item) => item.value == modifiedData.value?.mwAccountGroupId
      );
      if (accountGroup) selectedAccountGroup.value = accountGroup;
      else selectedAccountGroup.value = null;
    }
  }
}

function handleAccountGroupSelection(item: SelectDropdownItem) {
  if (!modifiedData.value) return;

  selectedAccountGroup.value = item ? item : null;
  modifiedData.value.mwAccountGroupId = item ? (item.value as string) : '';
}

const validationModel = ref({
  name: modifiedData.value.name || '',
  mwAccountGroupId: modifiedData.value.mwAccountGroupId || '',
});

const { t: $t } = useI18n({ useScope: 'global' });

const rules = computed(() => ({
  name: { required: helpers.withMessage($t('messages.errors.requiredField'), required) },
  mwAccountGroupId: {
    required: helpers.withMessage($t('messages.errors.requiredField'), required),
  },
}));

const $v = useVuelidate(rules, validationModel, { $lazy: true });

function onSave() {
  $v.value.$touch();
  if ($v.value.$invalid) return;
  if (props.group.recordMode !== RecordMode.Added)
    modifiedData.value.recordMode = RecordMode.Modified;
  emit('save', modifiedData.value);
}

function onDismissChanges() {
  emit('closeModal');
}

watch(
  () => modifiedData.value,
  (newData) => {
    validationModel.value.name = newData.name || '';
    validationModel.value.mwAccountGroupId = newData.mwAccountGroupId || '';
    const valueChanged = JSON.stringify(newData) !== JSON.stringify(props.group);
    isModified.value = valueChanged;
  },
  { deep: true }
);
</script>
